/**
 * Applies a sanitized bundle to the running app.
 *
 * Settings go through the zustand `persist` envelope in `localStorage`,
 * followed by `store.persist.rehydrate()`. That is deliberate: rehydrate is
 * the one call that re-runs each store's own `migrate` + `merge` +
 * `onRehydrateStorage`, so imported values land normalized (`themeMode`
 * recomputes `isDarkMode` and repaints the DOM, `aiTerminalSettings` runs
 * through `normalizeAiTerminalSettings`) with no per-field wiring here that
 * could drift from the manifest — and with no restart.
 *
 * Atomicity: every raw value is snapshotted, every write is computed and
 * applied, and the FIRST failure restores all of them before rethrowing. A
 * rehydrate failure is different — storage is already consistent by then, so
 * undoing it would be worse than reporting `restartNeeded`.
 *
 * MetaInfo is replayed LAST and per record (`metainfo:import` is scoped to one
 * `(connectionId, database)` pair), so a MetaInfo failure can never leave the
 * settings half-applied.
 */
import type { SettingsBundleGroupId } from './settingsBundleManifest';
import type { SettingsBundle } from './settingsBundleTypes';

export type StoreTarget = {
  read: () => string | null;
  write: (raw: string) => void;
  remove: () => void;
  /** The store's live `persist` version, so a created envelope is not migrated from 0. */
  version: () => number;
  rehydrate: () => Promise<void> | void;
};

export type ApplyTargets = {
  store: (storeKey: string) => StoreTarget | undefined;
  importMetaInfo?: (
    connectionId: string,
    database: string,
    json: string,
    mode: 'merge' | 'replace',
  ) => Promise<unknown>;
};

export type ApplyResult = {
  appliedGroups: SettingsBundleGroupId[];
  /** True when storage was updated but a store could not re-read it live. */
  restartNeeded: boolean;
  metaInfo?: { applied: number; total: number; failed: number };
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Keys whose imported value REPLACES the local one outright.
 *
 * Key-by-key merging is right for settings groups, where every field is an
 * independent preference. It is wrong for a value that describes ONE
 * arrangement: merging an imported `{ primary }` into a local
 * `{ primary, hidden }` keeps the local hidden list under the imported row and
 * produces a toolbar neither machine ever had, silently. Replace instead —
 * "import my toolbar" means the sender's toolbar, whole. Keyboard-shortcut
 * `overrides` are the same kind of value: merging would keep local overrides
 * the sender never had (and an exported empty map would clear nothing).
 */
const REPLACE_WHOLE: ReadonlySet<string> = new Set(['toolbarCustomization', 'overrides']);

/** Objects merge key-by-key; everything else (including arrays) replaces. */
function deepMerge(base: unknown, patch: unknown): unknown {
  if (!isRecord(base) || !isRecord(patch)) return patch;
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    out[key] = key in base && !REPLACE_WHOLE.has(key) ? deepMerge(base[key], value) : value;
  }
  return out;
}

function parseEnvelope(raw: string | null, version: number) {
  if (raw === null) return { state: {} as Record<string, unknown>, version };
  try {
    const parsed = JSON.parse(raw);
    if (isRecord(parsed) && isRecord(parsed.state)) {
      return {
        state: parsed.state,
        version: typeof parsed.version === 'number' ? parsed.version : version,
      };
    }
  } catch {
    /* fall through — a corrupt envelope is replaced, not merged into */
  }
  return { state: {} as Record<string, unknown>, version };
}

export async function applySettingsBundle(args: {
  bundle: SettingsBundle;
  groupIds: readonly SettingsBundleGroupId[];
  includeMetaInfo?: boolean;
  targets: ApplyTargets;
}): Promise<ApplyResult> {
  const { bundle, groupIds, targets } = args;

  // 1. Gather every write, keyed by store, merging groups that share a store.
  const patches = new Map<string, Record<string, unknown>>();
  const appliedGroups: SettingsBundleGroupId[] = [];
  for (const groupId of groupIds) {
    const payload = bundle.groups[groupId];
    if (!payload) continue;
    let touched = false;
    for (const [storeKey, fields] of Object.entries(payload)) {
      if (!targets.store(storeKey)) continue;
      patches.set(storeKey, deepMerge(patches.get(storeKey) ?? {}, fields) as Record<string, unknown>);
      touched = true;
    }
    if (touched) appliedGroups.push(groupId);
  }

  // 2. Snapshot, then write. The first failure restores everything.
  const snapshots = new Map<string, string | null>();
  try {
    for (const [storeKey, fields] of patches) {
      const target = targets.store(storeKey);
      if (!target) continue;
      snapshots.set(storeKey, target.read());
      const envelope = parseEnvelope(target.read(), target.version());
      target.write(
        JSON.stringify({
          state: deepMerge(envelope.state, fields),
          version: envelope.version,
        }),
      );
    }
  } catch (error) {
    for (const [storeKey, raw] of snapshots) {
      const target = targets.store(storeKey);
      if (!target) continue;
      if (raw === null) target.remove();
      else target.write(raw);
    }
    throw error;
  }

  // 3. Re-read live. Storage is already consistent, so a failure here is a
  //    "restart to finish", never a rollback.
  let restartNeeded = false;
  for (const storeKey of patches.keys()) {
    try {
      await targets.store(storeKey)?.rehydrate();
    } catch (error) {
      console.warn('[settings-transfer] rehydrate failed for', storeKey, error);
      restartNeeded = true;
    }
  }

  const result: ApplyResult = { appliedGroups, restartNeeded };

  // 4. MetaInfo last, one call per record — `metainfo:import` is scoped to a
  //    single (connectionId, database) pair, so this cannot be one bulk call.
  if (args.includeMetaInfo && bundle.metaInfo && targets.importMetaInfo) {
    let applied = 0;
    let failed = 0;
    for (const record of bundle.metaInfo) {
      try {
        await targets.importMetaInfo(
          String(record.connectionId),
          String(record.database),
          JSON.stringify(record),
          'merge',
        );
        applied += 1;
      } catch (error) {
        console.warn('[settings-transfer] meta-info import failed', error);
        failed += 1;
      }
    }
    result.metaInfo = { applied, total: bundle.metaInfo.length, failed };
  }

  return result;
}
